import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { toast } from "sonner";

export interface TrialAnalysis {
  engagement_score: number;
  risk_level: 'low' | 'medium' | 'high';
  conversion_probability: number;
  recommended_actions: string[];
}

export interface MilestoneProgress {
  milestone_id: string;
  milestone_name: string;
  progress_percentage: number;
  is_completed: boolean;
}

export interface PersonalizedOffer {
  offer_type: string;
  discount_percentage?: number;
  bonus_features?: string[];
  urgency_message: string;
  cta_text: string;
  personalization_factors: string[];
}

export const useTrialManagement = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  // Get trial analysis
  const {
    data: trialAnalysis,
    isLoading: isLoadingAnalysis,
    refetch: refetchAnalysis,
  } = useQuery({
    queryKey: ['trial-analysis', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;

      const { data, error } = await supabase.functions.invoke('trial-management', {
        body: {
          action: 'analyze_trial',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
        },
      });

      if (error) throw error;
      return data as TrialAnalysis;
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get milestone progress
  const {
    data: milestones,
    isLoading: isLoadingMilestones,
  } = useQuery({
    queryKey: ['trial-milestones', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;

      const { data, error } = await supabase.functions.invoke('trial-management', {
        body: {
          action: 'check_milestones',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
        },
      });

      if (error) throw error;
      return data.milestones as MilestoneProgress[];
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Get personalized offer
  const {
    data: personalizedOffer,
    isLoading: isLoadingOffer,
    refetch: refetchOffer,
  } = useQuery({
    queryKey: ['personalized-offer', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;

      const { data, error } = await supabase.functions.invoke('conversion-engine', {
        body: {
          action: 'generate_personalized_offer',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
        },
      });

      if (error) throw error;
      return data.offer as PersonalizedOffer;
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Track engagement
  const trackEngagement = useMutation({
    mutationFn: async (engagementData: {
      feature_used: string;
      session_duration_minutes: number;
      actions_performed: number;
      value_realized: number;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.functions.invoke('trial-management', {
        body: {
          action: 'track_engagement',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          ...engagementData,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trial-analysis'] });
    },
  });

  // Track funnel stage
  const trackFunnelStage = useMutation({
    mutationFn: async (stageData: {
      funnel_stage: string;
      stage_data?: any;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.functions.invoke('conversion-engine', {
        body: {
          action: 'track_funnel_stage',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          ...stageData,
        },
      });

      if (error) throw error;
      return data;
    },
  });

  // Trigger intervention
  const triggerIntervention = useMutation({
    mutationFn: async (interventionData: {
      intervention_type: string;
      trigger_condition: string;
      intervention_data?: any;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.functions.invoke('trial-management', {
        body: {
          action: 'trigger_intervention',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          ...interventionData,
        },
      });

      if (error) throw error;
      return data;
    },
  });

  // Extend trial
  const extendTrial = useMutation({
    mutationFn: async (extensionData: {
      extension_days: number;
      reason: string;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.functions.invoke('trial-management', {
        body: {
          action: 'extend_trial',
          workspace_id: currentWorkspace.id,
          ...extensionData,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Trial extended until ${new Date(data.new_expiry).toLocaleDateString()}`);
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  // Track conversion event
  const trackConversionEvent = useMutation({
    mutationFn: async (eventData: {
      event_type: string;
      event_data?: any;
    }) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');

      const { data, error } = await supabase.functions.invoke('conversion-engine', {
        body: {
          action: 'trigger_conversion_event',
          workspace_id: currentWorkspace.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          ...eventData,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      if (variables.event_type === 'conversion_complete') {
        toast.success('Welcome to your upgraded account!');
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      }
    },
  });

  return {
    // Data
    trialAnalysis,
    milestones,
    personalizedOffer,
    
    // Loading states
    isLoadingAnalysis,
    isLoadingMilestones,
    isLoadingOffer,
    
    // Actions
    trackEngagement,
    trackFunnelStage,
    triggerIntervention,
    extendTrial,
    trackConversionEvent,
    refetchAnalysis,
    refetchOffer,
  };
};